// src/Master/DeliveryBoy.js
import React, { useState, useEffect } from "react";
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Space, 
  Typography, 
  Card, 
  Tooltip, 
  message,
  Popconfirm
} from "antd";
import { 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  LockOutlined
} from "@ant-design/icons";
import { motion } from "framer-motion";
import BASEURL from "../API/Config";

const { Title, Text } = Typography;

const DeliveryBoyManagement = () => {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState("");

  const token = localStorage.getItem("token");

  const fetchWithToken = async (endpoint, options = {}) => {
    return fetch(`${BASEURL}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  };

  const fetchDeliveryBoys = async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken("/user/getdeliverboy");
      if (!response.ok) throw new Error("Failed to fetch delivery boys");
      const data = await response.json();
      setDeliveryBoys(data);
    } catch (error) {
      message.error("Failed to load delivery boys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryBoys();
  }, []);

  const handleShowModal = (deliveryBoy = null) => {
    if (deliveryBoy) {
      setIsEditing(true);
      setEditingId(deliveryBoy._id);
      form.setFieldsValue(deliveryBoy);
    } else {
      setIsEditing(false);
      setEditingId("");
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const url = isEditing ? `/user/update/${editingId}` : "/user/crbl";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetchWithToken(url, {
        method,
        body: JSON.stringify(values),
      });

      if (!response.ok) throw new Error("Failed to save data");

      message.success(isEditing ? "Updated successfully!" : "Added successfully!");
      fetchDeliveryBoys();
      handleCloseModal();
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetchWithToken(`/user/delveryBoy/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete delivery boy");

      message.success("Deleted successfully!");
      fetchDeliveryBoys();
    } catch (error) {
      message.error("Failed to delete delivery boy");
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Delivery Personnel",
      key: "name",
      render: (record) => (
        <Space>
          <div style={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            background: 'var(--primary-color)', 
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600
          }}>
            {record.firstname[0]}{record.lastname[0]}
          </div>
          <Text strong>{record.firstname} {record.lastname}</Text>
        </Space>
      ),
    },
    {
      title: "Contact Information",
      key: "contact",
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text size="small"><PhoneOutlined /> {record.phoneNumber}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}><MailOutlined /> {record.email}</Text>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (record) => (
        <Space>
          <Tooltip title="Edit">
            <Button 
              shape="circle" 
              icon={<EditOutlined />} 
              onClick={() => handleShowModal(record)} 
              style={{ color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure to delete this personnel?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button 
                shape="circle" 
                icon={<DeleteOutlined />} 
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Flipkart-style page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Delivery Boy Management</h2>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleShowModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}
        >
          Add Delivery Boy
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <Table
          columns={columns}
          dataSource={deliveryBoys}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} personnel` }}
          size="middle"
        />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? "Edit Delivery Boy" : "Register Delivery Boy"}</span>}
        open={showModal}
        onCancel={handleCloseModal}
        footer={null}
        centered
        width={580}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="firstname" label="First Name" rules={[{ required: true }]}>
              <Input prefix={<UserOutlined />} placeholder="First name" size="large" />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name" rules={[{ required: true }]}>
              <Input prefix={<UserOutlined />} placeholder="Last name" size="large" />
            </Form.Item>
          </div>
          <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true }]}>
            <Input prefix={<PhoneOutlined />} placeholder="e.g., 9876543210" size="large" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Account password" size="large" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? "Update" : "Register"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryBoyManagement;

